import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme as useMuiTheme,
  alpha,
  TextField
} from '@mui/material';
import {
  CalendarToday,
  ArrowDropDown,
  Check,
  Warning
} from '@mui/icons-material';
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
  subWeeks,
  subMonths,
  format,
  differenceInDays,
  parseISO,
  isValid
} from 'date-fns';

export type DatePreset = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

export interface DateRange {
  startDate: Date;
  endDate: Date;
  preset: DatePreset;
}

interface DateRangeSelectorProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
  showWarningForLargeRanges?: boolean;
  maxDaysWithoutWarning?: number;
}

const presetOptions = [
  {
    value: 'today' as DatePreset,
    label: 'Today',
    sublabel: 'Current day',
    icon: 'calendar_today',
    color: '#4ECDC4' /* basil-todo */
  },
  {
    value: 'yesterday' as DatePreset,
    label: 'Yesterday',
    sublabel: 'Previous day',
    icon: 'calendar_month',
    color: '#95A5A6' /* basil-todo */
  },
  {
    value: 'week' as DatePreset,
    label: 'This Week',
    sublabel: 'Last 7 days',
    icon: 'date_range',
    color: '#3498DB' /* basil-todo */
  },
  {
    value: 'month' as DatePreset,
    label: 'This Month',
    sublabel: 'Last 30 days',
    icon: 'calendar_view_month',
    color: '#9B59B6' /* basil-todo */
  },
  {
    value: 'custom' as DatePreset,
    label: 'Custom Range',
    sublabel: 'Pick specific dates',
    icon: 'tune',
    color: '#E67E22' /* basil-todo */
  }
];

/**
 * Get date range for a preset value
 */
export function getDateRangeForPreset(preset: DatePreset): DateRange {
  const now = new Date();

  switch (preset) {
    case 'today':
      return {
        startDate: startOfDay(now),
        endDate: endOfDay(now),
        preset: 'today'
      };
    case 'yesterday':
      const yesterday = subDays(now, 1);
      return {
        startDate: startOfDay(yesterday),
        endDate: endOfDay(yesterday),
        preset: 'yesterday'
      };
    case 'week':
      return {
        startDate: startOfWeek(now, { weekStartsOn: 1 }),
        endDate: endOfDay(now),
        preset: 'week'
      };
    case 'month':
      return {
        startDate: startOfMonth(now),
        endDate: endOfDay(now),
        preset: 'month'
      };
    default:
      return {
        startDate: startOfDay(now),
        endDate: endOfDay(now),
        preset: 'today'
      };
  }
}

/**
 * DateRangeSelector component for invoice timeline filtering.
 * Returns actual Date objects for use with ISO timestamp API parameters.
 */
const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  value,
  onChange,
  className = '',
  showWarningForLargeRanges = true,
  maxDaysWithoutWarning = 30
}) => {
  const theme = useMuiTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Custom date inputs
  const [customStartStr, setCustomStartStr] = useState('');
  const [customEndStr, setCustomEndStr] = useState('');
  const [showCustomInputs, setShowCustomInputs] = useState(false);

  // Initialize custom date strings when value changes
  useEffect(() => {
    if (value.preset === 'custom') {
      setCustomStartStr(format(value.startDate, 'yyyy-MM-dd'));
      setCustomEndStr(format(value.endDate, 'yyyy-MM-dd'));
      setShowCustomInputs(true);
    }
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setShowCustomInputs(false);
  };

  const handlePresetSelect = (preset: DatePreset) => {
    if (preset === 'custom') {
      setShowCustomInputs(true);
      // Initialize with current range or today
      if (value.preset !== 'custom') {
        setCustomStartStr(format(value.startDate, 'yyyy-MM-dd'));
        setCustomEndStr(format(value.endDate, 'yyyy-MM-dd'));
      }
    } else {
      const newRange = getDateRangeForPreset(preset);
      onChange(newRange);
      handleClose();
    }
  };

  const handleCustomApply = () => {
    const startDate = parseISO(customStartStr);
    const endDate = parseISO(customEndStr);

    if (isValid(startDate) && isValid(endDate)) {
      onChange({
        startDate: startOfDay(startDate),
        endDate: endOfDay(endDate),
        preset: 'custom'
      });
      handleClose();
    }
  };

  // Calculate days in range for warning
  const daysInRange = useMemo(() => {
    return differenceInDays(value.endDate, value.startDate) + 1;
  }, [value]);

  const showWarning = showWarningForLargeRanges && daysInRange > maxDaysWithoutWarning;

  // Get current preset label
  const currentOption = presetOptions.find(opt => opt.value === value.preset);
  const displayLabel = value.preset === 'custom'
    ? `${format(value.startDate, 'MMM d')} - ${format(value.endDate, 'MMM d')}`
    : currentOption?.label || 'Select Range';

  return (
    <Box className={className}>
      <Button
        variant="outlined"
        onClick={handleClick}
        endIcon={<ArrowDropDown />}
        startIcon={<CalendarToday sx={{ fontSize: 18 }} />}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          px: 2,
          py: 1,
          borderColor: alpha(theme.palette.divider, 0.3),
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.05)
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {displayLabel}
          </Typography>
          {showWarning && (
            <Warning sx={{ fontSize: 16, color: theme.palette.warning.main }} />
          )}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxWidth: 320,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            mt: 1
          }
        }}
      >
        {!showCustomInputs ? (
          <>
            {presetOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handlePresetSelect(option.value)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: 1,
                  mx: 1,
                  my: 0.5,
                  '&:hover': {
                    backgroundColor: alpha(option.color, 0.08)
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha(option.color, 0.1),
                      color: option.color,
                      fontSize: '0.875rem'
                    }}
                  >
                    <CalendarToday sx={{ fontSize: 16 }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={option.label}
                  secondary={option.sublabel}
                  primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
                {value.preset === option.value && (
                  <Check sx={{ color: theme.palette.primary.main, ml: 1 }} />
                )}
              </MenuItem>
            ))}
          </>
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Custom Date Range
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={customStartStr}
                onChange={(e) => setCustomStartStr(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={customEndStr}
                onChange={(e) => setCustomEndStr(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowCustomInputs(false)}
                  sx={{ flex: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCustomApply}
                  sx={{ flex: 1 }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {showWarning && !showCustomInputs && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Chip
                icon={<Warning sx={{ fontSize: 16 }} />}
                label={`${daysInRange} days selected - Performance may be affected`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ width: '100%' }}
              />
            </Box>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default DateRangeSelector;
